import { useState, useEffect, useRef } from 'react';
import { useNotificationStore, type Notification } from '@/store/notificationStore';

/* ─── Icone per tipo notifica ────────────────── */
const typeIcons: Record<string, string> = {
  box_joined: '📦',
  box_completed: '✅',
  payout_received: '💰',
  referral_converted: '🤝',
  subscription_activated: '💳',
  badge_earned: '🏆',
  system_pause: '⚠️',
  admin_message: '📢',
  welcome: '👋',
  box_filling: '📊',
};

/* ─── NotificationBell ───────────────────────── */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, fetchNotifications, fetchUnreadCount, markRead, markAllRead, deleteNotification } =
    useNotificationStore();

  // Fetch on mount + poll every 30s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    setOpen(!open);
    if (!open) {
      setError(null);
      fetchNotifications().catch(() => setError('Impossibile caricare le notifiche'));
    }
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ora';
    if (mins < 60) return `${mins}m fa`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h fa`;
    return `${Math.floor(hours / 24)}g fa`;
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-xl text-surface-500 hover:text-surface-700 hover:bg-surface-50 dark:text-surface-400 dark:hover:text-white dark:hover:bg-surface-800 transition-colors"
        aria-label="Notifiche"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-xl z-50 animate-in">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-surface-900 px-4 py-3 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-surface-900 dark:text-white">
              Notifiche {unreadCount > 0 && <span className="text-primary-500">({unreadCount})</span>}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead()}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                Segna tutte lette
              </button>
            )}
          </div>

          {/* List */}
          {error ? (
            <div className="px-4 py-8 text-center text-red-400 text-sm">
              ⚠️ {error}
            </div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-surface-400 dark:text-surface-500 text-sm">
              <span className="text-3xl block mb-2">🔔</span>
              Nessuna notifica
            </div>
          ) : (
            <div className="divide-y divide-surface-100 dark:divide-surface-800">
              {notifications.map((n: Notification) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors cursor-pointer ${
                    !n.read_at ? 'bg-primary-50/50 dark:bg-primary-500/5' : ''
                  }`}
                  onClick={() => !n.read_at && markRead(n.id)}
                >
                  <div className="flex gap-3">
                    <span className="text-lg flex-shrink-0">{typeIcons[n.type] ?? '🔔'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm truncate ${!n.read_at ? 'font-semibold text-surface-900 dark:text-white' : 'text-surface-600 dark:text-surface-400'}`}>
                          {n.title}
                        </p>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                          className="text-surface-400 hover:text-red-500 text-xs flex-shrink-0"
                          title="Elimina"
                        >
                          ×
                        </button>
                      </div>
                      <p className="text-xs text-surface-600 dark:text-surface-300 mt-0.5 line-clamp-2">
                        {n.body}
                      </p>
                      <p className="text-[10px] text-surface-400 dark:text-surface-500 mt-1">
                        {timeAgo(n.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
